import { Brain, Heart, Leaf, Users, Sparkles, ShieldCheck } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const DOMAINS = [
  { icon: Brain, title: "Mental Capacity", body: "Emotional regulation, self-awareness, resilience — the inner skills to navigate life." },
  { icon: Heart, title: "Practical Life", body: "Cooking, daily routines, healthy habits, self-care — the everyday foundations of independence." },
  { icon: Leaf, title: "Connection to Nature", body: "Outdoor activity that regulates, restores, and builds memory beyond four walls." },
  { icon: Users, title: "Community Participation", body: "The social skills to belong, contribute and form healthy relationships." },
  { icon: Sparkles, title: "Engagement & Motivation", body: "Building the willingness to show up — especially with participants who arrive resistant." },
  { icon: ShieldCheck, title: "Safety & Trust", body: "Trauma-informed practice, clear boundaries, and a place where participants feel safe enough to grow." },
];

export default function Approach() {
  return (
    <div data-testid="page-approach">
      <section className="relative bg-warm-50 pt-24 pb-20 lg:pt-32 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.approach} alt="Yoga group" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-warm-50/30 via-warm-50/85 to-warm-50" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 lg:px-12">
          <p className="text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-6">Our holistic approach</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-ink-900 max-w-4xl">
            <em className="text-brand not-italic">Six areas of skill.</em> One person. <br className="hidden lg:block" />
            One coordinated plan.
          </h1>
          <p className="mt-7 text-lg lg:text-xl leading-relaxed text-ink-500 max-w-3xl">
            A better life isn't built from a single intervention — it grows from a network of small, learned
            capacities. Our holistic approach develops skills across six interconnected areas, so participants
            leave with the practical, social and emotional capabilities to live the life they want.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {DOMAINS.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.05}>
                <div
                  data-testid={`domain-${d.title.toLowerCase()}`}
                  className="rounded-2xl bg-warm-50 border border-warm-200/60 p-10 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/5 transition-all h-full"
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand mb-6">
                    <d.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-serif text-2xl text-ink-900 mb-3">{d.title}</h3>
                  <p className="text-ink-500 leading-relaxed">{d.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-warm-100 py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-7">What we believe</p>
            <blockquote className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-ink-900 tracking-tight italic">
              "Everyone has the capacity to build new skills — and a better life follows from that."
            </blockquote>
            <p className="mt-8 text-sm uppercase tracking-widest text-ink-400">— The Neurogility approach</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
